// @flow
import {type SagaIterator} from 'redux-saga';
import {FULFILLED} from 'redux-promise-middleware';
import Store from 'store/storages/sessionStorage';
import {scroller} from 'react-scroll';

import {PRODUCT, LOCATION_CHANGED, APP} from 'constants/actions';

import {sessionStore} from 'libs/storage/storage';
import {createEventChannel} from 'libs/eventChannel/eventChannel';
import {takeEvery, takeLatest, call, select, put, fork, take, delay} from 'redux-saga/effects';
import {getPayment} from 'pages/cart/store/actions/cart';
import {saveRecentUrl} from 'features/quickCheckout/reducers/checkout/quick/quick';
import {RECENT_PDP_URL, PDP_SCROLL_TO_BOTTOM} from 'features/quickCheckout/constants/quickCheckout';
import {isProductPage as isProductPageSelector} from 'features/quickCheckout/selectors/router/router';

import {recoverySaga} from './recovery/recovery';

/**
 * Toggles PDP scroll to bottom CTA flag
 * @param {boolean} value - flag value
 */
export function* togglePDPScrollToBottomFlag(value: boolean): SagaIterator<> {
    yield call([sessionStore, 'set'], PDP_SCROLL_TO_BOTTOM, value);
}

/**
 * Save the current URL to pass it for returning button on the checkout page
 * @param {boolean} [isRx=false] - rx flag (should scroll to bottom flag be enabled or shouldn't)
 */
export function* prepareReturningFromCheckout(isRx: boolean = false): SagaIterator<> {
    yield call([Store, 'write'], RECENT_PDP_URL, window.location.pathname);
    yield put(saveRecentUrl(window.location.pathname));
    yield call(togglePDPScrollToBottomFlag, isRx);
}

/**
 * Fetches payment data
 */
export function* fetchPaymentData(): SagaIterator<> {
    const getPaymentAction = yield call(getPayment);
    yield put(getPaymentAction);
}

/**
 * Scrolls to bottom CTA when scroll flag is active
 */
export function* scrollBottomOnPDP(): SagaIterator<> {
    const isScrollToBottom = yield call([sessionStore, 'get'], PDP_SCROLL_TO_BOTTOM, false);

    if (isScrollToBottom) {
        yield delay();
        yield call([scroller, 'scrollTo'], 'wizardBottomCTA', {
            isDynamic: true,
            smooth: true,
            duration: 1000,
            offset: -300,
        });
    }
}

/**
 * Removes scroll to bottom CTA flag on location change
 */
export function* onLocationChanged(): SagaIterator<> {
    yield take(`${APP.GET_ROUTER_INFO}_${FULFILLED}`);
    const isProductPage = yield select(isProductPageSelector);

    if (!isProductPage) {
        yield call(togglePDPScrollToBottomFlag, false);
    }
}

/**
 * Start of quick checkout saga
 */
export function* quickCheckoutSaga(): SagaIterator<> {
    const nativeRedirect = yield call(createEventChannel, 'postMessage:redirect');

    yield takeEvery(PRODUCT.OPENED, fetchPaymentData);
    yield takeEvery(PRODUCT.OPENED, scrollBottomOnPDP);
    yield takeLatest(LOCATION_CHANGED, onLocationChanged);
    yield takeLatest(nativeRedirect, onLocationChanged);

    yield fork(recoverySaga);
}
